/* eslint-disable no-use-before-define */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import moment from 'moment';
import qs from 'qs';
import useInterval from 'hooks/useInterval';
import SessionStore from 'stores/SessionStore';
import Container from 'components/v2/container';
import FilterButton from 'components/v2/buttons/FilterButton';
import MultiSelectShipment from 'components/site/multi-shipment-search';
import MultiSelectUser from 'components/site/multi-user-search';
import DownloadIcon from 'components/v2/icons/DownloadIcon';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import {
  ADD_POD,
  ADD_DOCUMENTS,
  COMPLETE_STOP,
  CONFIRM_APPT,
  DEFAULT_FILTERS,
  SCHEDULE_APPT,
  SORTING_OPTIONS,
  ENTER_REQUIRED_DATES,
  TABS,
} from 'constants/next-actions';
import { useHistory, useLocation } from 'react-router-dom';
import Alert from 'components/ui/Alert';
import Badge from 'components/v2/Badge';
import ChevronDownIcon from 'components/v2/icons/ChevronDownIcon';
import ChevronRightIcon from 'components/v2/icons/ChevronRightIcon';
import DateRangeFilter from 'components/time/DateRangeFilter';
import { uniq } from 'underscore';
import {
  getShipments,
  getNextActions,
  getLFDAlertAction,
  getEmptyPickupReadyAction,
  triggerExportCSV,
  getMissingPodAction,
} from 'utils/ShipmentWebAPIUtils';
import CargomaticPane from 'components/v2/pane';
import FilterGroup from 'components/modules/equipment/FilterGroup';
import { showToast } from 'stores/ToastStore';
import { Disclosure } from '@headlessui/react';
import {
  findUsers,
  findAssociatedAccounts,
  getUserContextGrants,
} from 'utils/UserWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import ShipmentClosedModal from 'components/ui/modals/ShipmentClosedModal';
import RatingEngineModal from 'components/ui/modals/RatingEngineModal';
import { titleize } from 'underscore.string';
import { unescapeSpecialChars } from 'src/main/js/helpers/helper_functions';
import { getDefaultDateRangeFilter } from 'helpers/date-range';
import { sluggify } from 'utils/localization/utils';
import ShipmentLink from './ShipmentLink';
import NextActionsList from './NextActionsList';
import usePageVisibility from '../../../hooks/usePageVisibility';
import { getCircleButtonClass } from '../../v2/buttons/CircleButton';

const REQUIREMENT_TAB = !featureEnabled(CONFIGURED_FEATURES.SSR)
  ? ADD_POD
  : ADD_DOCUMENTS;

const PER_PAGE = 10;

const REFRESH_INTERVAL = 120000;

const DEFAULT_DATE_RANGE_VALUES = {
  isDashboard: true,
  startValue: 15,
  startTimeUnit: 'days',
  startKey: { date_type: 'pickup' },
};

const defaultDateRangeFilter = getDefaultDateRangeFilter(
  DEFAULT_DATE_RANGE_VALUES,
);

export default function CarrierDashboard() {
  const { t } = useTranslation();
  const history = useHistory();
  const thislocation = useLocation();
  const { search } = thislocation;
  const [locationParsed, setLocationParsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState(COMPLETE_STOP);
  const [actions, setActions] = useState({
    [REQUIREMENT_TAB]: [],
    [COMPLETE_STOP]: [],
    [CONFIRM_APPT]: [],
    [ENTER_REQUIRED_DATES]: [],
    [SCHEDULE_APPT]: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lfdAlerts, setLfdAlerts] = useState([]);
  const [ldfAlertsTotal, setLfdAlertsTotal] = useState(0);
  const [emptyPickupAlerts, setEmptyPickupAlerts] = useState([]);
  const [emptyPickupTotal, setEmptyPickupTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({
    [REQUIREMENT_TAB]: 1,
    [COMPLETE_STOP]: 1,
    [CONFIRM_APPT]: 1,
    [ENTER_REQUIRED_DATES]: 1,
    [SCHEDULE_APPT]: 1,
  });
  const [total, setTotal] = useState({
    [REQUIREMENT_TAB]: 0,
    [COMPLETE_STOP]: 0,
    [CONFIRM_APPT]: 0,
    [ENTER_REQUIRED_DATES]: 0,
    [SCHEDULE_APPT]: 0,
  });
  const [sortValue, setSortValue] = useState(SORTING_OPTIONS[0].value);
  const [filtersToggled, setFiltersToggled] = useState(false);
  const [filters, setFilters] = useState([...DEFAULT_FILTERS]);
  const [filterValues, setFilterValues] = useState({
    carrier: SessionStore.isAdmin() ? [] : [SessionStore.getUser()._id],
    shipper: SessionStore.isAdmin() ? [] : [SessionStore.getUser()._id],
    shipment: [],
    ...defaultDateRangeFilter,
  });
  const [scopes, setScopes] = useStateCallback(null);

  const [filterMetadata, setFilterMetadata] = useState({
    carrier: SessionStore.isAdmin() ? [] : [SessionStore.getUser()],
    shipper: SessionStore.isAdmin() ? [] : [SessionStore.getUser()],
    shipment: [],
  });

  const [cancelTokenSource, setCancelTokenSource] = useState();

  const [showTotal, setShowTotal] = useState(true);
  const [showEmailRecipientsModal, setShowEmailRecipientsModal] =
    useState(false);
  const [emailRecipientsForCSV, setEmailRecipientsForCSV] = useState('');
  const [selectedFiltersCount, setSelectedFiltersCount] = useState(0);
  const [tabsWithCounts, setTabsWithCounts] = useState(TABS);
  const [closedShipment, setClosedShipment] = useState(); // represents last shipment closed in NextActionList
  const [selectedAction, setSelectedAction] = useState();
  const [shipmentUpdatedOnModalClose, setShipmentUpdatedOnModalClose] =
    useState(false);
  const pathIncludesModalVal = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const [ratingEngineModal, setRatingEngineModal] = useState(
    !!pathIncludesModalVal.confirmLaneRates ||
      !!pathIncludesModalVal.confirmAccessorialRates,
  );
  const [ratingEngineModalType, setRatingEngineModalType] = useState(
    pathIncludesModalVal.confirmLaneRates === 'true' ||
      pathIncludesModalVal.confirmAccessorialRates === 'true',
  );

  const [ratingEngineModalRateType, setRatingEngineModalRateType] = useState(
    pathIncludesModalVal.confirmLaneRates === 'true' ||
      pathIncludesModalVal.confirmLaneRates === 'false'
      ? 'LaneRates'
      : 'LaneAccessorialRates',
  );

  const [ratingEngineModalFrom, setRatingEngineModalFrom] = useState(
    pathIncludesModalVal.confirmLaneRates ? '' : 'Accessorial',
  );
  const [ratingEngineModalToken, setRatingEngineModalToken] = useState(
    pathIncludesModalVal.token ? pathIncludesModalVal.token : '',
  );

  function handleHideTotal() {
    setShowTotal(false);
  }

  function cancelRequest() {
    if (cancelTokenSource) {
      cancelTokenSource.cancel();
    }
  }

  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, []);

  const isVisible = usePageVisibility(true);

  useInterval(fetchNextActions, REFRESH_INTERVAL);

  useEffect(() => {
    setFilterItems();
    setFiltersFromQuery();
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (locationParsed) {
      fetchLfdData();
      fetchEmptyPickupReady();
    }
  }, [locationParsed, actionType, filterValues]);

  useEffect(() => {
    if (locationParsed) {
      fetchNextActions();
    }
  }, [locationParsed, page, sortValue, filterValues]);

  useEffect(() => {
    document.title = t('CARRIER_DASHBOARD_TITLE');
  }, []);

  useEffect(() => {
    updateSelectedFiltersCount();
  }, [filterValues]);

  useEffect(() => {
    async function retrieveScopes() {
      const shipmentScopes = await getUserContextGrants(['shipments']);
      if (shipmentScopes) {
        const scopes = shipmentScopes?.entity?.scopes?.shipments;
        if (scopes) {
          setScopes(scopes, (scopes) => {
            if (scopes) setFilterItems(scopes);
          });
        }
      }
    }
    if (SessionStore.isCarrier() && !scopes) {
      retrieveScopes();
    }
  });

  useEffect(() => {
    if (
      selectedAction === null &&
      !isModalOpen &&
      shipmentUpdatedOnModalClose
    ) {
      handleReloadActions();
    }
  }, [selectedAction, isModalOpen, shipmentUpdatedOnModalClose]);

  function setFilterItems(scopes) {
    if (SessionStore.isAdmin()) {
      setFilters([
        {
          key: 'carrier',
          customerType: 'carriers',
          type: 'customer',
          name: 'CARRIER',
          placeholder: 'Select a carrier...',
        },
        {
          key: 'shipper',
          customerType: 'shippers',
          type: 'customer',
          name: 'SHIPPER',
        },
        ...DEFAULT_FILTERS,
      ]);
    } else {
      setFilters([
        {
          key: 'carrier',
          customerType: 'carriers',
          type: 'customer',
          name: 'CARRIER',
        },
        ...DEFAULT_FILTERS,
      ]);
    }
  }

  function useStateCallback(initialState) {
    const [state, setState] = useState(initialState);
    const cbRef = useRef(null); // init mutable ref container for callbacks

    const setStateCallback = useCallback((state, cb) => {
      cbRef.current = cb; // store current, passed callback in ref
      setState(state);
    }, []); // keep object reference stable, exactly like `useState`

    useEffect(() => {
      // cb.current is `null` on initial render,
      // so we only invoke callback on state *updates*
      if (cbRef.current) {
        cbRef.current(state);
        cbRef.current = null; // reset callback after execution
      }
    }, [state]);

    return [state, setStateCallback];
  }

  function updateSelectedFiltersCount() {
    const count = Object.keys(filterValues).reduce((count, key) => {
      if ((filterValues[key] || []).length > 0) {
        return count + 1;
      }

      if (key === 'origin' && filterValues[key]) {
        return count + 1;
      }

      if (key === 'destination' && filterValues[key]) {
        return count + 1;
      }

      return count;
    }, 1); // 1 for the date that's filtered from the start
    setSelectedFiltersCount(count);
  }

  async function setFiltersFromQuery() {
    const query = qs.parse(thislocation.search, { ignoreQueryPrefix: true });
    if (query.action_type && query.action_type.length > 0)
      setActionType(query.action_type || COMPLETE_STOP);
    if (query.page && query.page.length > 0)
      setPage(parseInt(query.page, 10) || 1);
    if (
      query.sort &&
      query.sort.length > 0 &&
      query.sort_dir &&
      query.sort_dir.length > 0
    ) {
      setSortValue(
        [query.sort, query.sort_dir].join('__') || SORTING_OPTIONS[0].value,
      );
    }

    setFilterValues((prev) => {
      const startDate =
        query.start_date && query.start_date.length > 0
          ? moment(query.start_date).startOf('day')
          : prev.start_date;
      const endDate =
        query.end_date && query.end_date.length > 0
          ? moment(query.end_date).endOf('day')
          : prev.end_date;

      let shipment = [];
      if (query.shipment && query.shipment.length > 0) {
        shipment = query.shipment;
      }

      let carrier = [];
      if (query.carrier && query.carrier.length > 0) {
        carrier = query.carrier;
      }

      let shipper = [];
      if (query.shipper && query.shipper.length > 0) {
        shipper = query.shipper;
      }
      return {
        start_date: startDate,
        end_date: endDate,
        date_type: query.date_type || 'pickup',
        shipment,
        carrier,
        shipper,
      };
    });

    let carriers = [];
    let shippers = [];
    let shipments = [];
    if (query.shipment && query.shipment.length > 0) {
      try {
        const {
          entity: { items },
        } = await getShipments({ _id: query.shipment });
        shipments = [...items];
      } catch (e) {
        console.error(e);
      }
    }
    if (SessionStore.isAdmin() && query.carrier && query.carrier.length > 0) {
      const fetchedCarriers = await findUsers({ _id: query.carrier });
      carriers = [...fetchedCarriers.entity.items];
    }

    if (SessionStore.isCarrier()) {
      const fetchedCarriers = await findAssociatedAccounts(
        { _id: query.carrier },
        'shipments',
      );
      carriers = [SessionStore.getUser(), ...fetchedCarriers.entity.items];
    }

    if (SessionStore.isAdmin() && query.shipper && query.shipper.length > 0) {
      const fetchedShippers = await findUsers({ _id: query.shipper });
      shippers = [...fetchedShippers.entity.items];
    }

    setFilterMetadata((prev) => {
      return {
        ...prev,
        shipment: [...shipments],
        carrier: [...carriers],
        shipper: [...shippers],
      };
    });

    setLocationParsed(true);
  }

  function updateUrl(filters) {
    const [sort, sort_dir] = sortValue.split('__');
    history.push(
      `${thislocation.pathname}?${qs.stringify({
        action_type: actionType,
        per_page: PER_PAGE,
        page,
        sort,
        sort_dir,
        ...filters,
      })}`,
    );
  }

  function handlePageClick(selectedPage) {
    setPage(selectedPage);
  }

  function handleTabClick(selectedActionType) {
    return function () {
      setActionType(selectedActionType);
      // reset pagination
      setPage(1);
    };
  }

  function handleSort(e) {
    setSortValue(e.target.value);
    setPage(1);
  }

  function getMomentDate(date) {
    if (!date) {
      return null;
    }
    return moment(date);
  }

  function translateFilterMetadata(currentFilters) {
    return {
      carrier: [...currentFilters.carrier],
      shipper: [...currentFilters.shipper],
      shipment: [...currentFilters.shipment],
      date_type: currentFilters.date_type,
      start_date: getMomentDate(currentFilters.start_date).format(),
      end_date: getMomentDate(currentFilters.end_date).format(),
    };
  }

  async function fetchNextActions() {
    if (!isVisible || isModalOpen) {
      return;
    }
    try {
      setLoading(true);
      if (!SessionStore.isCarrier() && !SessionStore.isAdmin()) {
        return;
      }
      const [sort, sort_dir] = sortValue.split('__');

      if (cancelTokenSource) {
        cancelRequest();
      }

      const source = axios.CancelToken.source();
      const cancelToken = source.token;
      setCancelTokenSource(source);

      const results = await getNextActions({
        actionType,
        per_page: PER_PAGE,
        page,
        sort,
        sort_dir,
        filterMetadata: translateFilterMetadata(filterValues),
        cancelToken,
      });
      setPages({
        [REQUIREMENT_TAB]: results[REQUIREMENT_TAB].pages,
        [COMPLETE_STOP]: results[COMPLETE_STOP].pages,
        [CONFIRM_APPT]: results[CONFIRM_APPT].pages,
        [ENTER_REQUIRED_DATES]: results[ENTER_REQUIRED_DATES].pages,
        [SCHEDULE_APPT]: results[SCHEDULE_APPT].pages,
      });
      setTotal({
        [REQUIREMENT_TAB]: results[REQUIREMENT_TAB].total,
        [COMPLETE_STOP]: results[COMPLETE_STOP].total,
        [CONFIRM_APPT]: results[CONFIRM_APPT].total,
        [ENTER_REQUIRED_DATES]: results[ENTER_REQUIRED_DATES].total,
        [SCHEDULE_APPT]: results[SCHEDULE_APPT].total,
      });
      setActions({
        [REQUIREMENT_TAB]: results[REQUIREMENT_TAB].actions,
        [COMPLETE_STOP]: results[COMPLETE_STOP].actions,
        [CONFIRM_APPT]: results[CONFIRM_APPT].actions.map((a) => ({
          ...a,
          ...{
            nextStop: {
              action: t('CONFIRM_APPT_ACTION', {
                nextStop_action: t(sluggify(a.nextStop.action)),
              }),
              _id: a.nextStop._id,
            },
          },
        })),
        [ENTER_REQUIRED_DATES]: results[ENTER_REQUIRED_DATES].actions,
        [SCHEDULE_APPT]: results[SCHEDULE_APPT].actions.map((a) => ({
          ...a,
          ...{
            nextStop: {
              action: t('SCHEDULE_APPT_ACTION', {
                nextStop_action: t(sluggify(a.nextStop.action)),
              }),
              _id: a.nextStop._id,
            },
          },
        })),
      });
      setTabsWithCounts((prevTabs) => {
        const newTabs = prevTabs.map((t) => {
          t.count = results[t.name].total;
          return t;
        });
        return newTabs;
      });
    } catch (err) {
      showToast(
        t('FETCH_NEXT_ACTIONS_FAILED'),
        t('FETCH_NEXT_ACTIONS_FAILED_MESSAGE'),
        'error',
      );
    } finally {
      setLoading(false);
      setCancelTokenSource(null);
    }
  }

  async function fetchLfdData() {
    try {
      const {
        meta: { total },
        shipments,
      } = await getLFDAlertAction(translateFilterMetadata(filterValues));
      setLfdAlerts(shipments);
      setLfdAlertsTotal(total);
    } catch (err) {
      console.error(err);
      showToast(t('FETCH_LFD_FAILED'), t('FETCH_LFD_FAILED_MESSAGE'), 'error');
    }
  }

  async function fetchEmptyPickupReady() {
    try {
      const {
        meta: { total },
        shipments,
      } = await getEmptyPickupReadyAction(
        translateFilterMetadata(filterValues),
      );
      setEmptyPickupTotal(total);
      setEmptyPickupAlerts(shipments);
    } catch (e) {
      console.error(e);
      showToast(
        t('FETCH_EMPTY_PICKUP_FAILED'),
        t('FETCH_EMPTY_PICKUP_FAILED'),
        'error',
      );
    }
  }

  function handleReloadActions() {
    setTimeout(() => {
      fetchNextActions();
      setShipmentUpdatedOnModalClose(false);
    }, 0);
  }

  function renderSort() {
    return (
      <div className="flex items-center w-52 mr-2">
        <select className="form-select" value={sortValue} onChange={handleSort}>
          {sortValue === 'Select' && (
            <option key={-1} value="Select">
              {t('SELECT')}
            </option>
          )}
          {SORTING_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.name)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  function renderContact() {
    return (
      <a
        key="support"
        href="mailto:support@cargomatic.com"
        className="flex-grow-0 flex-shrink-0 nav-item cursor button button-green mobile-carrier-nav-bar offers-offerlist heap"
      >
        <div className="text-center">
          <span>{t('CONTACT_SUPPORT')}</span>
        </div>
      </a>
    );
  }

  function handleToggleFilters() {
    setFiltersToggled((prev) => !prev);
  }

  function handleFilterActions(filterGroup, item, append = true) {
    const key = filterGroup.key;
    if (filterGroup.type === 'date') {
      updateUrl(translateFilterMetadata({ ...filterValues, [key]: item }));
      return;
    }
    if (append) {
      setFilterMetadata((prev) => ({
        ...prev,
        [key]: uniq([...prev[key], item]),
      }));

      updateUrl(
        translateFilterMetadata({
          ...filterValues,
          [key]: uniq([...filterValues[key], item._id]),
        }),
      );
    } else {
      setFilterMetadata((prev) => ({
        ...prev,
        [key]: prev[key].filter((el) => el._id !== item._id),
      }));
      updateUrl(
        translateFilterMetadata({
          ...filterValues,
          [key]: filterValues[key].filter((id) => id !== item._id),
        }),
      );
    }
  }

  const onDateRangeChange = ({ filterType, start, end }) => {
    setFilterValues((prev) => {
      const date_type = filterType === 'clear' ? 'pickup' : filterType;
      return {
        ...prev,
        date_type,
        start_date: start,
        end_date: end,
      };
    });
  };

  function renderMultiShipmentSearch(filterGroup) {
    const key = filterGroup.key;
    return (
      <MultiSelectShipment
        onSelect={(_value, item) => {
          handleFilterActions(filterGroup, item, true);
        }}
        onRemove={(item) => {
          handleFilterActions(filterGroup, item, false);
        }}
        selectedItems={filterMetadata[key]}
      />
    );
  }

  function renderMultiCarrierSearch(filterGroup) {
    const { key, customerType } = filterGroup;
    const type = t(key.toUpperCase());
    const placeholder = t('SEARCH_FOR_TYPE', {
      type,
    });
    if (SessionStore.isCarrier()) {
      return (
        <MultiSelectUser
          type={customerType}
          placeholder={placeholder}
          onSelect={(_value, item) => {
            handleFilterActions(filterGroup, item, true);
          }}
          disabled={!(scopes && scopes.hasMultiAccountAccess)}
          onRemove={(item) => {
            handleFilterActions(filterGroup, item, false);
          }}
          selectedItems={filterMetadata[key]}
        />
      );
    }
    return (
      <MultiSelectUser
        type={customerType}
        placeholder={placeholder}
        onSelect={(_value, item) => {
          handleFilterActions(filterGroup, item, true);
        }}
        onRemove={(item) => {
          handleFilterActions(filterGroup, item, false);
        }}
        selectedItems={filterMetadata[key]}
      />
    );
  }

  function renderDateFilters(filterGroup) {
    const key = filterGroup.key;
    return (
      <div key={key}>
        <DateRangeFilter
          className="dropdown-filter-wrapper"
          onChange={onDateRangeChange}
          key="date-range-filter"
          buttonClassName="w-18 justify-center flex button button-green"
          label={
            <span className="flex">
              {t('DATE', { count: 1 })}
              <ChevronDownIcon className="font-bold h-4 w-4 ml-1 my-auto" />
            </span>
          }
          startKey="pickup"
          initialStart={defaultDateRangeFilter.start_date}
          initialEnd={defaultDateRangeFilter.end_date}
          zIndex="99999"
          popoverPlacement="right"
          customSearchFields={['pickup', 'deliver', 'created']}
        />
      </div>
    );
  }

  function renderFilterGroup(filterGroup) {
    if (filterGroup.type === 'customer') {
      return renderMultiCarrierSearch(filterGroup);
    }

    if (filterGroup.type === 'shipment') {
      return renderMultiShipmentSearch(filterGroup);
    }

    if (filterGroup.type === 'date') {
      return renderDateFilters(filterGroup);
    }

    return null;
  }

  function renderFilterGroups() {
    return filters.map((filterGroup) => {
      const title = titleize(t(filterGroup.name, { count: 1 }));
      const groupId = filterGroup.key;
      const renderedFilterGroup = renderFilterGroup(filterGroup);
      return (
        <FilterGroup title={title} key={groupId}>
          {renderedFilterGroup}
        </FilterGroup>
      );
    });
  }

  function renderFilters() {
    return (
      <div className={sideBarClassName}>
        <div className="p-4">
          <h4>{t('FILTER', { count: 3 })}</h4>
          {renderFilterGroups()}
        </div>
      </div>
    );
  }

  const sideBarClassName = classnames({
    'w-full': true,
    'filters-sidebar': true,
    'bg-gray-50': true,
    'h-full': true,
  });

  function renderDrawer() {
    const { t } = useTranslation();
    return (
      <CargomaticPane
        container={null}
        show={filtersToggled}
        title={t('FILTER', { count: 2 })}
        onHide={() => setFiltersToggled(false)}
        renderBody={renderFilters}
        footer={null}
        className="sm:w-1/3 sm:max-w-none"
      />
    );
  }

  function renderShipments(shipments = []) {
    return shipments.map((shipment) => (
      <React.Fragment key={shipment._id}>
        <tr>
          <td>
            <ShipmentLink shipment={shipment} />
          </td>
        </tr>
      </React.Fragment>
    ));
  }

  const lfdAlertClassName = classnames({
    'w-full': !total[actionType],
    flex: true,
    'justify-between': true,
    'items-center': true,
    'space-x-2': true,
  });

  function renderBanner(
    data = [],
    dataTotal = 0,
    messageSlug = '',
    subTitleSlug = '',
    emptyMessageSlug = '',
    alertType = 'error',
  ) {
    const currentCount = data.length;
    return (
      <Disclosure key="disclosure">
        {({ open }) => {
          return (
            <>
              <Disclosure.Button
                key="disclosure-button"
                className="focus:outline-none focus-visible:ring focus-visible:ring-yellow-500 focus-visible:ring-opacity-75 w-full"
              >
                <Alert type={alertType} className="w-full mb-4">
                  <div className="flex flex-wrap justify-between w-full">
                    {dataTotal > 0 ? (
                      <div className={lfdAlertClassName}>
                        <span className="hidden md:inline">
                          <Trans
                            i18nKey={messageSlug}
                            values={{ num: dataTotal }}
                            components={[<span className="font-bold" />]}
                          />
                        </span>
                        {open && (
                          <ChevronDownIcon className="font-bold h-6 w-6" />
                        )}
                        {!open && (
                          <ChevronRightIcon className="font-bold h-6 w-6" />
                        )}
                      </div>
                    ) : (
                      <div>{t(emptyMessageSlug)}</div>
                    )}
                  </div>
                </Alert>
              </Disclosure.Button>
              <Disclosure.Panel
                key="disclosure-panel"
                className="bg-white rounded-md shadow text-sm max-h-80 overflow-y-scroll"
              >
                {currentCount > 0 && (
                  <table className="default-table">
                    <thead>
                      <tr>
                        <th>{t(subTitleSlug)}</th>
                      </tr>
                    </thead>
                    <tbody>{renderShipments(data)}</tbody>
                  </table>
                )}
              </Disclosure.Panel>
            </>
          );
        }}
      </Disclosure>
    );
  }

  async function setSelectedActionShipment(selectedShipment) {
    const isShorthaulWithMissingPOD =
      selectedShipment?.shipment?.shipment_type === 'shorthaul' &&
      [ADD_DOCUMENTS, ADD_POD].includes(selectedShipment?.action);

    if (isShorthaulWithMissingPOD) {
      const shipmentId = selectedShipment?.shipment?._id;
      const {
        data: { data = {} },
      } = await getMissingPodAction({ shipment: shipmentId });

      const shipment = data?.actions?.length
        ? data.actions[0]
        : selectedShipment;
      setSelectedAction(shipment);
    } else {
      setSelectedAction(selectedShipment);
    }
  }

  function renderAlertBanner() {
    const showCompleteStopReminder =
      total[COMPLETE_STOP] > 0 && total[REQUIREMENT_TAB] > 0;
    return (
      <div className="w-full mx-auto">
        {renderBanner(
          lfdAlerts,
          ldfAlertsTotal,
          'SHIPMENTS_PAST_LFD_WITH_COUNT',
          'SHIPMENT_PAST_LFD',
          'NO_SHIPMENT_FOR_LFD',
          'error',
        )}
        {renderBanner(
          emptyPickupAlerts,
          emptyPickupTotal,
          'SHIPMENTS_EMPTY_PICKUP_WITH_COUNT',
          'SHIPMENT_WITH_EMPTY_PICKUP',
          'NO_SHIPMENT_FOR_EMPTY_PICKUP',
          'warning',
        )}
        {showCompleteStopReminder && (
          <Alert type="success" className="my-4">
            <div>{t('COMPLETE_SHIPMENTS_REMINDER')}</div>
          </Alert>
        )}
        {total[actionType] > 0 && showTotal && (
          <Alert type="info" className="my-4" onClose={handleHideTotal}>
            <div>
              <Trans
                i18nKey="SHIPMENTS_REQUIRE_ATTENTION"
                values={{ num: total[actionType] }}
                components={[<span className="font-bold" />]}
              />
            </div>
          </Alert>
        )}
      </div>
    );
  }

  function renderActionBar() {
    return (
      <div className="mt-6">
        <div className="w-full mx-auto p-4">
          <div className="flex items-center justify-between flex-wrap">
            <div className="w-0 flex-1 flex items-center flex-wrap mb-2 sm:mb-0 -mx-2">
              {tabsWithCounts.map((tab) => {
                const { name, color } = tab;
                const active = name === actionType;
                return (
                  <div className="px-2 relative" key={name}>
                    <Badge
                      color={color}
                      onClick={handleTabClick(name)}
                      active={active}
                    >
                      {t(name)}
                    </Badge>
                    {tab.count ? (
                      <Badge
                        className="absolute -right-3 -top-3"
                        color="green"
                        size="small"
                      >
                        {tab.count}
                      </Badge>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className="order-3 mt-2 flex-shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
              {renderSort()}
            </div>
            <div className="order-2 flex-shrink-0 sm:order-3 sm:ml-3">
              {selectedFiltersCount === 0 && (
                <FilterButton
                  onClick={handleToggleFilters}
                  active={filtersToggled}
                  size="small"
                />
              )}
              {selectedFiltersCount > 0 && (
                <button
                  className="button button-hollow-green"
                  onClick={handleToggleFilters}
                >
                  <div className="flex items-center space-x-1">
                    <div
                      className={getCircleButtonClass({
                        size: 'xsmall',
                        color: 'brand',
                      })}
                    >
                      {selectedFiltersCount}
                    </div>
                    <span>{t('FILTER(S)')}</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderEmptyList() {
    return <div className="p-4">{t('EMPTY_STATE')}</div>;
  }

  async function handleExportToCSV() {
    try {
      const [sort, sort_dir] = sortValue.split('__');
      const emailRecipients = emailRecipientsForCSV
        .split(',')
        .map((item) => item.trim());
      const response = await triggerExportCSV(
        {
          sort,
          sort_dir,
          ...translateFilterMetadata(filterValues),
        },
        emailRecipients,
      );
      showToast(t('EXPORT_CSV'), t('CSV_EXPORT_REQUEST_SUCCESS'), 'success');
      handleCloseEmailRecipientsModal();
    } catch (err) {
      showToast(t('EXPORT_CSV'), t('CSV_EXPORT_REQUEST_FAILED'), 'error');
      handleCloseEmailRecipientsModal();
    }
  }

  function getBody() {
    return (
      <div className="mb-4">
        <CargomaticInput
          type="textarea"
          rows={3}
          label={t('EMAIL_RECIPIENTS')}
          placeholder={t('ADD_EMAIL_WITH_COMMA')}
          value={emailRecipientsForCSV}
          onChange={(emails) => {
            setEmailRecipientsForCSV(emails);
          }}
        />
      </div>
    );
  }

  function getFooter() {
    return (
      <div className="flex items-center justify-end">
        <button
          className="button button-secondary"
          type="button"
          onClick={handleCloseEmailRecipientsModal}
        >
          {t('CANCEL')}
        </button>
        <button
          className="button button-green"
          onClick={handleExportToCSV}
          disabled={!emailRecipientsForCSV.length}
        >
          {t('CONFIRM')}
        </button>
      </div>
    );
  }

  function handleShowEmailRecipientsModal() {
    setShowEmailRecipientsModal(true);
  }

  function handleCloseEmailRecipientsModal() {
    setEmailRecipientsForCSV('');
    setShowEmailRecipientsModal(false);
  }

  function renderCSVEmailRecipientsModal() {
    return (
      <CargomaticModal
        title={t('EXPORT_CSV_FILE')}
        style={{ top: '30%' }}
        show={showEmailRecipientsModal}
        onHide={handleCloseEmailRecipientsModal}
        body={getBody()}
        footer={getFooter()}
      />
    );
  }

  function renderExportCSVButton() {
    return (
      <div className="flex flex-row-reverse">
        <button
          title={t('EXPORT_CSV')}
          className="flex item-center button button-hollow-green"
          onClick={handleShowEmailRecipientsModal}
        >
          {t('EXPORT_CSV')}
          <DownloadIcon />
        </button>
      </div>
    );
  }

  if (!SessionStore.isAdmin() && !SessionStore.isCarrier()) {
    return (
      <div className="p-4">
        <Alert title={t('REQUIRE_PERMISSION_TO_VIEW_PAGE')} type="error" />
      </div>
    );
  }

  return (
    <>
      <Container className="min-h-screen py-10">
        {SessionStore.isCarrier() && (
          <h2 className="my-0 mb-4">
            <Trans
              i18nKey="HELLO_CARRIER"
              values={{
                carrier_name: unescapeSpecialChars(SessionStore.getUser().name),
              }}
              components={[<span className="font-normal" />]}
            />
          </h2>
        )}
        {renderAlertBanner()}
        {renderExportCSVButton()}
        <div className="bg-white rounded-md shadow divide-y divide-solid divide-gray-200">
          {renderActionBar()}
          {total[actionType] > 0 && !loading && (
            <NextActionsList
              actions={actions[actionType]}
              actionType={actionType}
              isCustomerView={SessionStore.isCarrier()}
              onPageClick={handlePageClick}
              perPage={PER_PAGE}
              page={page}
              pages={pages[actionType]}
              total={total[actionType]}
              setIsModalOpen={setIsModalOpen}
              onShipmentClosed={setClosedShipment}
              selectedAction={selectedAction}
              setSelectedAction={setSelectedActionShipment}
              setShipmentUpdatedOnModalClose={setShipmentUpdatedOnModalClose}
            />
          )}
          {loading && (
            <div className="p-4">
              <LoadingCircle text={t('LOADING')} />
            </div>
          )}
          {!total[actionType] && !loading && renderEmptyList()}
        </div>
        <div className="mt-6 flex items-center justify-end">
          <div className="w-50">{renderContact()}</div>
        </div>
        {closedShipment && (
          <ShipmentClosedModal
            show={closedShipment}
            onAccessorialClick={() => {
              history.push(
                '/shipments/' + closedShipment._id + '?action=payout',
              );
            }}
            onCancelClick={() => setClosedShipment()}
          />
        )}
        {ratingEngineModal && (
          <RatingEngineModal
            show={ratingEngineModal}
            onCloseClick={() => setRatingEngineModal()}
            from={ratingEngineModalFrom}
            rateType={ratingEngineModalRateType}
            type={ratingEngineModalType}
            token={ratingEngineModalToken}
          />
        )}
      </Container>
      {renderDrawer()}
      {renderCSVEmailRecipientsModal()}
    </>
  );
}
